<script setup>
import {
  LMap,
  LTileLayer,
  LImageOverlay,
  LControlZoom,
  LMarker,
  LTooltip,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { useHolesView } from "@/scripts/Utilities/HolesView.js";
import SearchableSelect from "@/components/SearchableSelectComponent.vue";
import ImportCourseHolesModal from "@/components/Dashboard/Utilities/Holes/ImportCourseHolesModal.vue";
import ClearCourseHolesModal from "@/components/Dashboard/Utilities/Holes/ClearCourseHolesModal.vue";

const {
  myMap,
  selectedCourse,
  holes,
  hole,
  loading,
  showCourseMap,
  zoom,
  center,
  url,
  opacity,
  overlay,
  mapImage,
  bounds,
  icon,
  selectedHole,
  targetCourse,
  boundsMapAndFlags,
  courses,
  mapRotation,
  showImportModal,
  showClearModal,
  enableSaveHole,
  enableSendToDevice,
  openImportHoles,
  openClearHoles,
  confirmClearHoles,
  centerUpdated,
  changeView,
  updateTee,
  updateFront,
  updateCenter,
  updateBack,
  saveHoles,
  sendConfig,
  applyBoundsAndOverlays,
} = useHolesView();
</script>

<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">Course holes:</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item"><a href="#">Dashboard</a></li>
              <li class="breadcrumb-item active">Devices</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <div class="card" id="course-config">
          <div class="card-body">
            <div class="box-body table-responsive no-padding">
              <div class="row">
                <div class="col-9">
                  <LMap
                    style="height: 650px; border-radius: 5px"
                    ref="myMap"
                    @update:center="centerUpdated"
                    :zoom="zoom"
                    :center="center"
                    :options="{
                      zoomControl: false,
                      attributionControl: false,
                      maxBounds: bounds.every((coord) =>
                        coord.every((val) => val !== 0)
                      )
                        ? bounds
                        : undefined,
                    }"
                  >
                    <LTileLayer :url="url"></LTileLayer>

                    <LImageOverlay
                      v-if="overlay"
                      :opacity="opacity"
                      :url="overlay"
                      :bounds="bounds"
                      :z-index="10"
                      :style="{ transform: `rotate(${mapRotation}deg)` }"
                    ></LImageOverlay>

                    <LImageOverlay
                      v-if="mapImage && showCourseMap"
                      :opacity="opacity"
                      :url="mapImage"
                      :bounds="bounds"
                      :style="{ transform: `rotate(${mapRotation}deg)` }"
                    ></LImageOverlay>

                    <LControlZoom position="topright"></LControlZoom>

                    <div v-if="selectedHole">
                      <LMarker
                        :lat-lng="selectedHole.startPoint"
                        :draggable="true"
                        @update:lat-lng="updateTee"
                        :icon="icon"
                      >
                        <LTooltip>Tee</LTooltip>
                      </LMarker>

                      <LMarker
                        :lat-lng="selectedHole.frontPoint"
                        :draggable="true"
                        @update:lat-lng="updateFront"
                        :icon="icon"
                      >
                        <LTooltip>Front</LTooltip>
                      </LMarker>

                      <LMarker
                        :lat-lng="selectedHole.centerPoint"
                        :draggable="true"
                        @update:lat-lng="updateCenter"
                        :icon="icon"
                      >
                        <LTooltip>Center</LTooltip>
                      </LMarker>

                      <LMarker
                        :lat-lng="selectedHole.endPoint"
                        :draggable="true"
                        @update:lat-lng="updateBack"
                        :icon="icon"
                      >
                        <LTooltip>Back</LTooltip>
                      </LMarker>
                    </div>
                  </LMap>
                </div>
                <div class="col-3">
                  <div class="col-12 text-center">
                    <div class="btn-group">
                      <button
                        @click="changeView(false)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': showCourseMap,
                          'btn-default': !showCourseMap,
                        }"
                      >
                        TM Map
                      </button>
                      <button
                        @click="changeView(true)"
                        type="button"
                        class="btn btn-sm"
                        :class="{
                          'btn-primary': !showCourseMap,
                          'btn-default': showCourseMap,
                        }"
                      >
                        Satellite
                      </button>
                    </div>
                    <hr />
                  </div>

                  <div class="col-12">
                    <div class="form-group">
                      <label>Select course:</label>
                      <SearchableSelect
                        v-model="targetCourse"
                        :options="courses"
                        :label="'name'"
                        :trackBy="'id'"
                        :placeholder="'Select the courses'"
                      />
                    </div>
                  </div>

                  <div class="col-12 d-flex align-items-start flex-column">
                    <button
                      class="btn btn-sm btn-info mt-2 mb-2"
                      @click="openImportHoles"
                      :disabled="!targetCourse"
                    >
                      Import Holes
                    </button>

                    <button
                      class="btn btn-sm btn-info mb-2"
                      @click="applyBoundsAndOverlays"
                      v-if="boundsMapAndFlags && targetCourse"
                    >
                      Reset Map View
                    </button>
                    <button
                      v-if="targetCourse"
                      class="btn btn-danger mt-2"
                      @click="openClearHoles"
                    >
                      Clear Holes
                    </button>
                  </div>

                  <div
                    class="col-12"
                    v-show="
                      selectedCourse != null && holes.length && targetCourse
                    "
                  >
                    <div class="form-group">
                      <label>Imported from:</label>
                      <div class="border border-secondary border-2 rounded p-2">
                        Course Holes
                      </div>
                    </div>
                  </div>

                  <div v-if="holes.length && targetCourse">
                    <hr />
                    <div class="form-group row">
                      <label class="col-4 col-form-label">Hole #</label>
                      <div class="col-8">
                        <select v-model="hole" class="custom-select">
                          <option
                            v-for="(myHole, i) in holes"
                            :key="i"
                            :value="i"
                          >
                            Hole {{ myHole.hole }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="form-group row">
                      <label class="col-4 col-form-label">Colour</label>
                      <div class="col-8">
                        <select
                          v-model="selectedHole.colour"
                          class="custom-select"
                        >
                          <option value="yellow">yellow</option>
                          <option value="transparent">transparent</option>
                          <option value="green">green</option>
                          <option value="blue">blue</option>
                          <option value="red">red</option>
                        </select>
                      </div>
                    </div>

                    <div class="form-group row">
                      <label class="col-4 col-form-label">Par</label>
                      <div class="col-8">
                        <input
                          type="number"
                          class="form-control"
                          v-model="selectedHole.par"
                        />
                      </div>
                    </div>

                    <div class="form-group row">
                      <label class="col-4 col-form-label">Display</label>
                      <div class="col-8">
                        <input
                          type="text"
                          class="form-control"
                          placeholder="(Optional)"
                          v-model="selectedHole.displayHole"
                        />
                      </div>
                    </div>
                    <div class="form-group row">
                      <button
                        @click="saveHoles"
                        :disabled="!enableSaveHole"
                        :class="{
                          'btn btn-primary': enableSaveHole,
                          'btn btn-secondary': !enableSaveHole,
                        }"
                        :style="{
                          width: '100%',
                          cursor: enableSaveHole ? 'pointer' : 'not-allowed',
                        }"
                        style="width: 100%"
                      >
                        Save holes
                      </button>
                    </div>
                    <div class="form-group row">
                      <button
                        @click="sendConfig"
                        :disabled="!enableSendToDevice"
                        :class="{
                          'btn btn-success': enableSendToDevice,
                          'btn btn-secondary': !enableSendToDevice,
                        }"
                        :style="{
                          width: '100%',
                          cursor: enableSendToDevice
                            ? 'pointer'
                            : 'not-allowed',
                        }"
                        style="width: 100%"
                      >
                        Send to devices
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="overlay" v-show="loading">
            <i class="fas fa-spinner fa-spin fa-lg"></i>
          </div>
        </div>
      </div>
    </section>

    <ImportCourseHolesModal v-model="showImportModal" :courses="courses" />

    <ClearCourseHolesModal
      v-model="showClearModal"
      :loading="loading"
      @confirm="confirmClearHoles"
    />
  </div>
</template>

<style scoped>
.overlay {
  z-index: 1000 !important;
  background-color: rgba(27, 30, 33, 0.7) !important;
}

.overlay i {
  color: white !important;
}

.import {
  border-radius: 0 3px 3px 0 !important;
}

/* Hide any Leaflet attribution */
:deep(.leaflet-control-attribution) {
  display: none !important;
}
</style>
